import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { ActivityView } from '@photos/shared';
import { useActivity, useActivityActors, useAlbums, useUndo } from '../../api/hooks';
import { Icon } from '../../components/Icon';
import { ACTION_GROUPS, activityQuery, filtersFromParams, filtersToParams, hasFilters, NO_FILTERS, type ActivityFilters, type DateRange } from '../../lib/activityFilters';
import { avatarColor, fmtRelative, initials } from '../../lib/format';
import { useToast } from '../../state/toasts';
import { undoMessage } from './studioUi';

interface ActivityEntriesProps {
  items: ActivityView[];
  /** show "Undo" on undoable entries */
  onUndo?(entry: ActivityView): void;
  undoing?: boolean;
  /** album names and photo counts link to the album (Studio) */
  linkAlbums?: boolean;
  compact?: boolean;
  /** text when there is nothing to show */
  empty?: { title: string; body: string };
}

/** The activity log as rows: who, what, when, album, and Undo where it's available. */
export function ActivityEntries({ items, onUndo, undoing, linkAlbums, compact, empty }: ActivityEntriesProps) {
  if (!items.length)
    return (
      <div className="empty" style={{ padding: compact ? '30px 10px' : undefined }}>
        <h3>{empty?.title ?? 'Nothing yet'}</h3>
        {empty?.body ?? 'Changes show up here as people work.'}
      </div>
    );
  return (
    <ol className={`activity${compact ? ' compact' : ''}`}>
      {items.map((e) => (
        <li key={e.id} className={e.undone ? 'undone' : undefined}>
          <span className="av" style={{ background: avatarColor(e.actorName) }} aria-hidden="true">
            {initials(e.actorName)}
          </span>
          <div className="what">
            <div>{e.summary}</div>
            <small>
              {fmtRelative(e.at)}
              {linkAlbums && e.albumId && e.count > 0 ? (
                <>
                  {' · '}
                  {/* One photo opens it in its album; more open the album. */}
                  <Link to={e.count === 1 && e.photoIds[0] ? `/studio/albums/${e.albumId}?photo=${encodeURIComponent(e.photoIds[0])}` : `/studio/albums/${e.albumId}`}>
                    {e.count === 1 ? '1 photo' : `${e.count.toLocaleString('en-US')} photos`}
                  </Link>
                </>
              ) : (
                e.count > 1 && ` · ${e.count.toLocaleString('en-US')} photos`
              )}
              {!compact && e.albumName && (
                <>
                  {' · '}
                  {linkAlbums && e.albumId ? <Link to={`/studio/albums/${e.albumId}`}>{e.albumName}</Link> : e.albumName}
                </>
              )}
              {e.undone && ` · undone by ${e.undone.byName} ${fmtRelative(e.undone.at)}`}
            </small>
          </div>
          {onUndo && e.undoable && (
            <button type="button" className="btn sm" disabled={undoing} onClick={() => onUndo(e)}>
              <Icon name="undo" />
              Undo
            </button>
          )}
        </li>
      ))}
    </ol>
  );
}

const RANGE_LABEL: Record<DateRange, string> = { any: 'Any time', today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', custom: 'Custom…' };

/**
 * Search and filters for the activity log. The full bar (Studio → Activity): text, person, actions, album, dates and
 * Reset filters (shown while any filter is set; clears them all in one click). Compact (album drawer, album fixed): text, actions and dates.
 */
function ActivityFilterBar({ value, onChange, compact }: { value: ActivityFilters; onChange(f: ActivityFilters): void; compact?: boolean }) {
  const actors = useActivityActors();
  const albums = useAlbums();
  const set = (patch: Partial<ActivityFilters>) => onChange({ ...value, ...patch });
  const people = actors.data ?? [];
  const albumList = albums.data?.items ?? [];
  const personName = people.find((p) => p.id === value.actorId)?.name ?? '';
  const albumName = albumList.find((a) => a.id === value.albumId)?.name ?? '';
  const toggleGroup = (key: string) => set({ groups: value.groups.includes(key) ? value.groups.filter((g) => g !== key) : [...value.groups, key] });
  return (
    <div className={`actfilters${compact ? ' compact' : ''}`} role="search" aria-label="Filter activity">
      <div className="row">
        <input
          className="inp sm"
          type="search"
          placeholder={compact ? 'Search this album’s activity' : 'Search: what happened, who, album, file name'}
          aria-label="Search activity"
          value={value.q}
          onChange={(e) => set({ q: e.target.value })}
        />
        {!compact && (
          <>
            <input
              className="inp sm"
              list="act-people"
              placeholder="Person"
              aria-label="Person"
              defaultValue={personName}
              key={`person-${value.actorId}-${people.length}`}
              onChange={(e) => {
                const v = e.target.value;
                const match = people.find((p) => p.name === v);
                if (match) set({ actorId: match.id });
                else if (!v) set({ actorId: '' });
              }}
            />
            <datalist id="act-people">
              {people.map((p) => (
                <option key={p.id} value={p.name} />
              ))}
            </datalist>
            <input
              className="inp sm"
              list="act-albums"
              placeholder="Album"
              aria-label="Album"
              defaultValue={albumName}
              key={`album-${value.albumId}-${albumList.length}`}
              onChange={(e) => {
                const v = e.target.value;
                const match = albumList.find((a) => a.name === v);
                if (match) set({ albumId: match.id });
                else if (!v) set({ albumId: '' });
              }}
            />
            <datalist id="act-albums">
              {albumList.map((a) => (
                <option key={a.id} value={a.name} />
              ))}
            </datalist>
          </>
        )}
        <select className="inp sm" aria-label="Date range" value={value.range} onChange={(e) => set({ range: e.target.value as DateRange })}>
          {(Object.keys(RANGE_LABEL) as DateRange[]).map((r) => (
            <option key={r} value={r}>
              {RANGE_LABEL[r]}
            </option>
          ))}
        </select>
        {value.range === 'custom' && (
          <>
            <input className="inp sm" type="date" aria-label="From" value={value.from} onChange={(e) => set({ from: e.target.value })} />
            <input className="inp sm" type="date" aria-label="To" value={value.to} onChange={(e) => set({ to: e.target.value })} />
          </>
        )}
        {hasFilters(value, compact) && (
          <button type="button" className="btn sm reset" onClick={() => onChange({ ...NO_FILTERS, albumId: compact ? value.albumId : '' })}>
            <Icon name="x" />
            Reset filters
          </button>
        )}
      </div>
      <div className="chips" role="group" aria-label="Actions">
        {ACTION_GROUPS.map((g) => (
          <button key={g.key} type="button" className={`fchip${value.groups.includes(g.key) ? ' on' : ''}`} aria-pressed={value.groups.includes(g.key)} onClick={() => toggleGroup(g.key)}>
            {g.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The log for some filters, with Undo and "Load more". */
export function ActivityList({ filters, albumId, compact }: { filters: ActivityFilters; albumId?: string; compact?: boolean }) {
  const query = useMemo(() => ({ ...activityQuery(filters), ...(albumId ? { albumId } : {}) }), [filters, albumId]);
  const activity = useActivity(query);
  const undo = useUndo();
  const toast = useToast();
  const items = activity.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = hasFilters(filters, !!albumId);
  if (activity.isError)
    return (
      <div className="empty">
        <h3>Couldn’t load activity</h3>
        {activity.error.message}
      </div>
    );
  if (!activity.data)
    return (
      <div className="sentinel">
        <div className="spinner" />
      </div>
    );
  return (
    <>
      {filtered && (
        <div className="actcount" aria-live="polite">
          {items.length.toLocaleString('en-US')}
          {activity.hasNextPage ? '+' : ''} {items.length === 1 ? 'entry' : 'entries'}
        </div>
      )}
      <ActivityEntries
        items={items}
        compact={compact}
        linkAlbums
        undoing={undo.isPending}
        empty={filtered ? { title: 'No matching activity', body: 'Try other filters, or reset them.' } : undefined}
        onUndo={(e) =>
          undo.mutate(e.id, {
            onSuccess: (r) => toast(undoMessage(r)),
            onError: (err) => toast({ icon: 'info', title: 'Couldn’t undo', sub: err.message }),
          })
        }
      />
      {activity.hasNextPage && (
        <div className="sentinel">
          <button type="button" className="btn sm" onClick={() => void activity.fetchNextPage()} disabled={activity.isFetchingNextPage}>
            Load more
          </button>
        </div>
      )}
    </>
  );
}

/** The album page's Activity drawer: the album's log with compact filters (text, actions, dates). */
export function AlbumActivity({ albumId, filters, onChange }: { albumId: string; filters: ActivityFilters; onChange(f: ActivityFilters): void }) {
  return (
    <>
      <ActivityFilterBar value={filters} onChange={onChange} compact />
      <ActivityList filters={filters} albumId={albumId} compact />
    </>
  );
}

/** `/studio/activity`: every change in Studio, newest first, with Undo; filters live in the URL so views can be shared. */
export function ActivityPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const change = (f: ActivityFilters) => setParams(filtersToParams(f), { replace: true });
  return (
    <>
      <div className="toolbar">
        <h1>Activity</h1>
        <span className="meta">Every change in Studio. Undo reverts exactly the photos that haven’t changed since.</span>
      </div>
      <ActivityFilterBar value={filters} onChange={change} />
      <ActivityList filters={filters} />
    </>
  );
}
